// ============================================
// Path: /frontend/src/pages/CreateListingPage.jsx
// UPDATED: Added tier checking before checkout
// CSS OPTIMIZED: Mobile-first responsive design
// ============================================

import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.jsx';
import { useToast } from '../hooks/useToast';
import { uploadAPI } from '../api/upload.api';
import { checkUserCanCreateListing } from '../utils/tierCheck';
import { counties } from '../lib/locations';
import { makes } from '../lib/vehicles';
import { vehicleTypes, propertyTypes } from '../lib/categories';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import ListingImage from '../components/ListingImage';
import UpgradeLimitModal from '../components/modals/UpgradeLimitModal';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Upload, Lightbulb, Loader2, X } from 'lucide-react';
import ChamaOptIn from '../components/listings/ChamaOptIn';
import ChamaHowItWorks from '../components/listings/ChamaHowItWorks';
import api from '../api/client';

export default function CreateListingPage() {
  console.log('🎨 [CreateListingPage] Component mounted');

  const { user, loading: authLoading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [county, setCounty] = useState('');
  const [subCounty, setSubCounty] = useState('');
  const [category, setCategory] = useState('');
  const [type, setType] = useState('');
  const [subType, setSubType] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');

  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const fileInputRef = useRef(null);
  const MAX_IMAGES = 5;

  const [tags, setTags] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [isUploadingImages, setIsUploadingImages] = useState(false);

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [tierInfo, setTierInfo] = useState(null);

  const [chamaEnabled, setChamaEnabled] = useState(false);
  const [chamaData, setChamaData] = useState({
    allowGroupBuy: false,
    totalShares: null,
    sharePrice: null,
    fundingDeadline: null
  });

      useEffect(() => {
      // If coming from a fresh navigation (not from checkout/delete), clear old data
      const comingFromCheckout = sessionStorage.getItem('comingFromCheckout');
      const isDeletingForNew = sessionStorage.getItem('deletingForNewListing');
      
      // ⭐ FIX: Don't clear if we're in the middle of auto-submitting
      if (!comingFromCheckout && !isDeletingForNew) {
        console.log('[CreateListingPage] Fresh start - clearing old form data');
        sessionStorage.removeItem('createListingFormData');
        sessionStorage.removeItem('pendingListing');
      } else {
        console.log('[CreateListingPage] Preserving form data (checkout or deletion in progress)');
        // Clean up the checkout flag only (keep deletingForNewListing for restoration)
        sessionStorage.removeItem('comingFromCheckout');
      }
      
      // Handle file picker cancel - reset spinner when window regains focus
      const handleFocus = () => {
        if (isUploadingImages) {
          setIsUploadingImages(false);
        }
      };

      window.addEventListener('focus', handleFocus);
      
      // Cleanup function
      return () => {
        window.removeEventListener('focus', handleFocus);
        
        // Give a tiny delay to check if we're going to checkout or upgrade
        setTimeout(() => {
          const isGoingToCheckout = sessionStorage.getItem('comingFromCheckout');
          const isUpgradingForListing = sessionStorage.getItem('upgradingForListing');
          const isDeletingForNew = sessionStorage.getItem('deletingForNewListing');

          // ⭐ FIX: Don't clear data if going to checkout, upgrading, OR deleting old listing
          if (!isGoingToCheckout && !isUpgradingForListing && !isDeletingForNew) {
            console.log('[CreateListingPage] User navigated away - clearing form data');
            sessionStorage.removeItem('createListingFormData');
            sessionStorage.removeItem('pendingListing');
          } else {
            console.log('[CreateListingPage] Preserving form data for payment flow');
          }
        }, 100);
      };
    }, [isUploadingImages]);


  useEffect(() => {
    const savedFormData = sessionStorage.getItem('createListingFormData');
    if (savedFormData) {
      console.log('[CreateListingPage] Restoring form data');
      const data = JSON.parse(savedFormData);
      setTitle(data.title || '');
      setDescription(data.description || '');
      setPrice(data.price || '');
      setCounty(data.county || '');
      setSubCounty(data.subCounty || '');
      setCategory(data.category || '');
      setType(data.type || '');
      setSubType(data.subType || '');
      setMake(data.make || '');
      setModel(data.model || '');
      setTags(data.tags || []);
      if (data.imagePreviews && data.imagePreviews.length > 0) {
        setImagePreviews(data.imagePreviews);
      }

      setChamaEnabled(data.chamaEnabled || false);
      setChamaData(data.chamaData || { allowGroupBuy: false, totalShares: null, sharePrice: null, fundingDeadline: null });

    // ⭐ NEW FIX: Auto-submit after deleting old listing
    // Submit directly from sessionStorage (like PaymentStatusPage does)
    if (sessionStorage.getItem('deletingForNewListing')) {
      console.log('[CreateListingPage] User deleted old listing - auto-submitting directly from sessionStorage');
      
      sessionStorage.removeItem('deletingForNewListing');
      
      const autoSubmitListing = async () => {
        try {
          setIsSubmitting(true);

          // 1. Upload images from base64
          let uploadedImageUrls = [];
          if (data.imagePreviews && data.imagePreviews.length > 0) {
            console.log('[CreateListingPage] Uploading', data.imagePreviews.length, 'images...');
            
            const imageFiles = await Promise.all(
              data.imagePreviews.map(async (base64, index) => {
                const response = await fetch(base64);
                const blob = await response.blob();
                return new File([blob], `restored-image-${index}.jpg`, { type: 'image/jpeg' });
              })
            );

            const uploadPromises = imageFiles.map(file => uploadAPI.uploadImage(file));
            const uploadResponses = await Promise.all(uploadPromises);
            uploadedImageUrls = uploadResponses.map(response => response.data?.url || response.url);
            console.log('[CreateListingPage] Images uploaded:', uploadedImageUrls);
          }

          // 2. Create listing data from sessionStorage (not React state)
          const listingData = {
            title: data.title,
            description: data.description,
            price: Number(data.price),
            county: data.county,
            subCounty: data.subCounty,
            category: data.category.toUpperCase(),
            type: data.type,
            subType: data.subType || null,
            tags: data.tags || [],
            imageUrl: uploadedImageUrls,
            allowGroupBuy: data.chamaData?.allowGroupBuy || false,
            totalShares: data.chamaData?.totalShares || null,
            sharePrice: data.chamaData?.sharePrice || null,
            fundingDeadline: data.chamaData?.fundingDeadline || null,
          };

          if (data.category === 'vehicle') {
            listingData.make = data.make;
            listingData.model = data.model;
          }

          console.log('[CreateListingPage] Creating listing directly from saved data:', listingData);

          // 3. Submit to API
          const response = await api.post('/listings', listingData);

          if (response.data.success) {
            console.log('[CreateListingPage] Listing created successfully after delete');

            // Clear all saved data
            sessionStorage.removeItem('createListingFormData');
            sessionStorage.removeItem('pendingListing');

            // Refresh user data
            await refreshUser();

            // Reset form state
            setTitle('');
            setDescription('');
            setPrice('');
            setCounty('');
            setSubCounty('');
            setCategory('');
            setType('');
            setSubType('');
            setMake('');
            setModel('');
            setImageFiles([]);
            setImagePreviews([]);
            setTags([]);
            setChamaEnabled(false);
            setChamaData({
              allowGroupBuy: false,
              totalShares: null,
              sharePrice: null,
              fundingDeadline: null
            });

            toast({
              title: 'Listing Created!',
              description: 'Your listing has been submitted for admin approval.',
            });

            setTimeout(() => {
              navigate('/my-listings');
            }, 1500);
          }
        } catch (error) {
          console.error('[CreateListingPage] Auto-submit error:', error);
          toast({
            variant: 'destructive',
            title: 'Submission Error',
            description: error.response?.data?.message || 'Failed to create listing. Please try again manually.',
          });
        } finally {
          setIsSubmitting(false);
        }
      };

      // Execute auto-submit
      autoSubmitListing();
    }
  }
}, []);

const saveFormData = () => {
  // Form restoration data (includes UI state like image previews)
  const formData = {
    title,
    description,
    price,
    county,
    subCounty,
    category,
    type,
    subType,
    make,
    model,
    tags,
    imagePreviews,
    chamaEnabled,
    chamaData,
  };
  
// ⭐ ADD THIS DEBUG LOG
  console.log('[CreateListingPage] Saving form data:', {
    title,
    county,
    subCounty,
    category,
    type,
    subType
  });

  // API-ready listing data (matches what would be sent to backend)
  const listingData = {
    title,
    description,
    price: Number(price),
    county,
    subCounty,
    category: category.toUpperCase(),
    type,
    subType: subType || null,
    tags,
    allowGroupBuy: chamaData.allowGroupBuy,
    totalShares: chamaData.totalShares,
    sharePrice: chamaData.sharePrice,
    fundingDeadline: chamaData.fundingDeadline,
    imageUrl: [], // Placeholder - will be uploaded after payment
  };
  
  if (category === 'vehicle') {
    listingData.make = make;
    listingData.model = model;
  }
  
  // Save both: formData for restoration, listingData for checkout
  sessionStorage.setItem('createListingFormData', JSON.stringify(formData));
  sessionStorage.setItem('pendingListing', JSON.stringify(listingData));
  
  console.log('[CreateListingPage] Form data saved to both keys');
};
  

  const handleTypeChange = (value) => {
  console.log('🔄 [CreateListingPage] Type changed:', value);
  setType(value);
  setSubType('');
};

  const handleCountyChange = (value) => {
    console.log('🔄 [CreateListingPage] County changed:', value);
    setCounty(value);
    setSubCounty('');
  };

  const handleMakeChange = (value) => {
    console.log('🔄 [CreateListingPage] Make changed:', value);
    setMake(value);
    setModel('');
  };

  const handleImageChange = (e) => {
    setIsUploadingImages(false);
    const files = Array.from(e.target.files || []);
    
    if (files.length === 0) return;

    const remainingSlots = MAX_IMAGES - imageFiles.length;
    
    if (files.length > remainingSlots) {
      toast({
        variant: 'destructive',
        title: 'Too many images',
        description: `You can only add ${remainingSlots} more image(s). Maximum is ${MAX_IMAGES}.`,
      });
      return;
    }

    // Validate file sizes BEFORE adding
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
    const oversizedFiles = files.filter(file => file.size > MAX_FILE_SIZE);
    
    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map(f => f.name).join(', ');
      const sizeMB = (oversizedFiles[0].size / (1024 * 1024)).toFixed(1);
      
      toast({
        variant: 'destructive',
        title: 'File Too Large',
        description: `Maximum size is 10MB. "${fileNames}" is ${sizeMB}MB. Please compress it first.`,
      });
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    console.log('🔵 [CreateListingPage] Images selected:', files.length);

    const newFiles = [...imageFiles, ...files];
    setImageFiles(newFiles);

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews((prev) => [...prev, reader.result]);
        console.log('✅ [CreateListingPage] Image preview loaded');
      };
      reader.readAsDataURL(file);
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImageRemove = (index) => {
    console.log('👤 [CreateListingPage] User removed image at index:', index);
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAISuggestions = async () => {
    console.log('👤 [CreateListingPage] User clicked AI suggestions');

    if (!description) {
      toast({
        variant: 'destructive',
        title: 'Description needed',
        description: 'Please write a description before getting AI suggestions.',
      });
      return;
    }

    console.log('🔵 [CreateListingPage] Fetching AI suggestions...', {
      descriptionLength: description.length,
    });

    setIsSuggesting(true);

    try {
      const words = description.toLowerCase().split(/\s+/);
      const commonWords = ['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by'];
      const newTags = words
        .filter(word => word.length > 4 && !commonWords.includes(word))
        .slice(0, 5)
        .filter(tag => !tags.includes(tag));

      if (newTags.length > 0) {
        setTags([...tags, ...newTags]);
        console.log('✅ [CreateListingPage] Tags generated:', newTags);
        toast({
          title: 'Tags Added!',
          description: `${newTags.length} tags have been added based on your description.`,
        });
      } else {
        toast({
          title: 'No New Tags',
          description: 'Could not generate additional tags from your description.',
        });
      }
    } catch (error) {
      console.error('❌ [CreateListingPage] AI suggestions error:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Could not generate tag suggestions. Please add tags manually.',
      });
    } finally {
      setIsSuggesting(false);
    }
  };

  const handleTagInput = (e) => {
    if (e.key === ',' || e.key === 'Enter') {
      e.preventDefault();
      const newTag = e.target.value.trim();
      if (newTag && !tags.includes(newTag)) {
        console.log('🔄 [CreateListingPage] Tag added:', newTag);
        setTags([...tags, newTag]);
      }
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('👤 [CreateListingPage] Form submitted');

    if (!user) {
      console.log('⚠️ [CreateListingPage] User not authenticated');
      toast({
        variant: 'destructive',
        title: 'Authentication Error',
        description: 'You must be logged in to create a listing.',
      });
      return;
    }

    if (imageFiles.length === 0) {
      console.log('⚠️ [CreateListingPage] No images selected');
      toast({
        variant: 'destructive',
        title: 'Image Required',
        description: 'Please upload at least one image for your listing.',
      });
      return;
    }

    console.log('🔵 [CreateListingPage] Checking tier limits...');
    const tierCheck = await checkUserCanCreateListing();

    if (!tierCheck.canCreate) {
      console.log('⚠️ [CreateListingPage] Cannot create listing:', tierCheck);
      
  saveFormData();
  
  if (tierCheck.requiresPayment) {
  if (tierCheck.isExpired) {
    // Expired subscription - can choose any plan
    toast({
      title: 'Subscription Expired',
      description: 'Your subscription has expired. Please choose a new plan to continue.',
    });
  } else {
    // First-time user
    toast({
      title: 'Subscription Required',
      description: 'Please select a subscription plan to create your first listing.',
    });
  }
  sessionStorage.setItem('comingFromCheckout', 'true'); // ⭐ ADD THIS
  navigate('/listings/checkout');
  return;
}
  
  // CASE 2: Active subscription but hit limits → Show upgrade modal
  sessionStorage.setItem('upgradingForListing', 'true');

  toast({
    variant: 'destructive',
    title: 'Tier Limit Reached',
    description: tierCheck.reason,
  });
  setTierInfo({
    tier: tierCheck.tier,
    currentUsage: tierCheck.currentUsage,
    limit: tierCheck.limit,
  });
  
  setShowUpgradeModal(true);
  return;
}

// ✅ USER HAS ACTIVE SUBSCRIPTION - CREATE LISTING DIRECTLY
console.log('✅ [CreateListingPage] Tier check passed - creating listing directly');
setIsSubmitting(true);

  try {
    console.log('🔵 [CreateListingPage] Uploading images...');
    const uploadPromises = imageFiles.map((file) => uploadAPI.uploadImage(file));
    const uploadResponses = await Promise.all(uploadPromises);
    const imageUrls = uploadResponses.map((response) => response.data?.url || response.url);
    console.log('✅ [CreateListingPage] All images uploaded:', imageUrls);

    const listingData = {
      title,
      description,
      price: Number(price),
      county,
      subCounty,
      category: category.toUpperCase(),
      type,
      tags,
      imageUrl: imageUrls,
      subType: subType || null,
      allowGroupBuy: chamaData.allowGroupBuy,
      totalShares: chamaData.totalShares,
      sharePrice: chamaData.sharePrice,
      fundingDeadline: chamaData.fundingDeadline,
    };

    if (category === 'vehicle') {
      listingData.make = make;
      listingData.model = model;
    }

    console.log('🔵 [CreateListingPage] Creating listing via API...');
    const response = await api.post('/listings', listingData);

    if (response.data.success) {
      console.log('✅ [CreateListingPage] Listing created successfully');

      // ⭐ CLEAR ALL FORM DATA - CRITICAL!
      sessionStorage.removeItem('createListingFormData');
      sessionStorage.removeItem('pendingListing');

      // ⭐ REFRESH USER TO GET UPDATED adsUsedThisMonth
      await refreshUser();

      // ⭐ RESET ALL FORM STATE
      setTitle('');
      setDescription('');
      setPrice('');
      setCounty('');
      setSubCounty('');
      setCategory('');
      setType('');
      setSubType('');
      setMake('');
      setModel('');
      setImageFiles([]);
      setImagePreviews([]);
      setTags([]);
      setChamaEnabled(false);
      setChamaData({
        allowGroupBuy: false,
        totalShares: null,
        sharePrice: null,
        fundingDeadline: null
      });

      toast({
        title: 'Listing Created!',
        description: 'Your listing has been submitted for admin approval.',
      });

      // Redirect to my listings
      setTimeout(() => {
        navigate('/my-listings');
      }, 1500);
    }



     } catch (error) {
      console.error('❌ [CreateListingPage] Submission error:', error);
      
      // Parse validation errors from backend
      let errorMessage = 'Could not proceed to checkout. Please check your network and try again.';
      
      if (error.response?.data?.errors && error.response.data.errors.length > 0) {
        errorMessage = error.response.data.errors[0].msg;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      toast({
        variant: 'destructive',
        title: 'Submission Error',
        description: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (authLoading || !user) {
    console.log('⏳ [CreateListingPage] Loading auth...');
    
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin" />
        </main>
        <Footer />
      </div>
    );
  }

  const subCounties = counties.find((c) => c.name === county)?.subCounties || [];
  const models = makes.find((m) => m.name === make)?.models || [];
  const propertySubTypes =
    propertyTypes.find((p) => p.name === type)?.subTypes || [];
  const showPropertySubTypes =
    category === 'property' && propertySubTypes.length > 0;

  console.log('🎨 [CreateListingPage] Rendering form with state:', {
    category,
    type,
    imageCount: imageFiles.length,
    tagsCount: tags.length,
  });

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      {/* CSS FIX 1: Page container padding - py-12 md:py-20 → py-6 sm:py-8 md:py-12 */}
      <main className="flex-grow container py-6 sm:py-8 md:py-12 px-4">
        {/* CSS FIX 2: Added px-4 for mobile side spacing (mx-auto centers the card) */}
        <Card className="max-w-3xl mx-auto">
          {/* CSS FIX 3: CardHeader responsive sizing and padding */}
          <CardHeader className="p-3 sm:p-4 md:p-6">
            <CardTitle className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold">
              Create a New Listing
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm md:text-base">
              Fill out the form below. Then choose a subscription plan.
              Applies to the first listing only.
            </CardDescription>
          </CardHeader>
          {/* CSS FIX 4: CardContent responsive padding */}
          <CardContent className="p-3 sm:p-4 md:p-6">
            {/* CSS FIX 5: Form spacing - space-y-6 → space-y-3 sm:space-y-4 md:space-y-6 */}
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4 md:space-y-6">

              {/* Image Upload */}
              {/* CSS FIX 6: Field spacing */}
              <div className="space-y-1.5 sm:space-y-2">
                {/* CSS FIX 7: Label sizing */}
                <Label className="text-xs sm:text-sm">Listing Images (Max {MAX_IMAGES})</Label>
                <Input
                  id="image"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageChange}
                  className="hidden"
                  ref={fileInputRef}
                />
                
                {imagePreviews.length > 0 ? (
                  <div className="space-y-2 sm:space-y-3">
                    {/* CSS FIX 9: Grid gap - gap-3 → gap-2 sm:gap-3 md:gap-4 */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3 md:gap-4">
                      {imagePreviews.map((preview, index) => (
                        <div key={index} className="relative group">
                          <ListingImage
                            src={preview}
                            alt={`Preview ${index + 1}`}
                            type={category}
                            className="rounded-md object-cover aspect-video w-full"
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="icon"
                            className="absolute top-1 right-1 h-6 w-6 sm:h-7 sm:w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => handleImageRemove(index)}
                          >
                            <X className="h-3 w-3 sm:h-4 sm:w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                    
                    <div className="flex items-center justify-between">
                      {/* CSS FIX 11: Text sizing */}
                      <span className="text-[10px] sm:text-xs md:text-sm text-muted-foreground">
                        {imageFiles.length} / {MAX_IMAGES} images uploaded
                      </span>
                      {imageFiles.length < MAX_IMAGES && (
                        /* CSS FIX 10: Button text sizing */
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="text-xs sm:text-sm h-8 sm:h-9"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <Upload className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                          Add More
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (

                  <div
                    className="w-full aspect-video border-2 border-dashed border-muted-foreground/30 rounded-md flex flex-col items-center justify-center cursor-pointer hover:bg-muted/50"
                    onClick={() => {
                      setIsUploadingImages(true);
                      fileInputRef.current?.click();
                    }}
                  >
                      {isUploadingImages ? (
                        <>
                          <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 text-blue-400 mb-2 animate-spin" />
                          <p className="text-xs sm:text-sm md:text-base text-muted-foreground">
                            Loading Images...
                          </p>
                        </>
                      ) : (
                      <>
                        {/* CSS FIX 11: Icon and text responsive sizing */}
                        <Upload className="w-6 h-6 sm:w-8 sm:h-8 text-muted-foreground mb-2" />
                        <p className="text-xs sm:text-sm md:text-base text-muted-foreground">
                          Click to upload images (max {MAX_IMAGES})
                        </p>
                        <p className="text-[10px] sm:text-xs text-muted-foreground/80">
                          PNG, JPG, or WEBP up to 5MB each
                        </p>
                      </>
                    )}
                  </div>
               
                )}
              </div>

              {/* Title */}
              <div className="space-y-1.5 sm:space-y-2">
                <Label htmlFor="title" className="text-xs sm:text-sm">Listing Title</Label>
                {/* CSS FIX 8: Input height and text size */}
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., 2023 Toyota Prado TX"
                  className="h-9 sm:h-10 text-xs sm:text-sm"
                  required
                />
              </div>

              {/* Description */}
              <div className="space-y-1.5 sm:space-y-2">
                <Label htmlFor="description" className="text-xs sm:text-sm">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Provide a detailed description of your item..."
                  className="text-xs sm:text-sm min-h-[80px] sm:min-h-[100px]"
                  required
                />
              </div>

              {/* Category and Price */}
              {/* CSS FIX 9: Grid gap */}
              <div className="grid md:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                <div className="space-y-1.5 sm:space-y-2">
                  <Label htmlFor="category" className="text-xs sm:text-sm">Category</Label>
                  <Select
                    onValueChange={(value) => {
                      setCategory(value);
                      setType('');
                      setSubType('');
                    }}
                    value={category}
                    required
                  >
                    <SelectTrigger id="category" className="h-9 sm:h-10 text-xs sm:text-sm">
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vehicle" className="text-xs sm:text-sm">Vehicle</SelectItem>
                      <SelectItem value="property" className="text-xs sm:text-sm">Property</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5 sm:space-y-2">
                  <Label htmlFor="price" className="text-xs sm:text-sm">Price (KES)</Label>
                  <Input
                    id="price"
                    type="number"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder="e.g., 5200000"
                    className="h-9 sm:h-10 text-xs sm:text-sm"
                    required
                  />
                </div>
              </div>

              {/* County and Sub-County */}
              <div className="grid md:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                <div className="space-y-1.5 sm:space-y-2">
                  <Label htmlFor="county" className="text-xs sm:text-sm">County</Label>
                  <Select
                    onValueChange={handleCountyChange}
                    value={county}
                    required
                  >
                    <SelectTrigger id="county" className="h-9 sm:h-10 text-xs sm:text-sm">
                      <SelectValue placeholder="Select a county" />
                    </SelectTrigger>
                    <SelectContent>
                      {counties.map((c) => (
                        <SelectItem key={c.name} value={c.name} className="text-xs sm:text-sm">
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5 sm:space-y-2">
                  <Label htmlFor="subCounty" className="text-xs sm:text-sm">Sub-County</Label>
                  <Select
                    onValueChange={setSubCounty}
                    value={subCounty}
                    required
                    disabled={!county}
                  >
                    <SelectTrigger id="subCounty" className="h-9 sm:h-10 text-xs sm:text-sm">
                      <SelectValue placeholder="Select a sub-county" />
                    </SelectTrigger>
                    <SelectContent>
                      {subCounties.map((sc) => (
                        <SelectItem key={sc} value={sc} className="text-xs sm:text-sm">
                          {sc}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Vehicle Make and Model */}
              {category === 'vehicle' && (
                <div className="grid md:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                  <div className="space-y-1.5 sm:space-y-2">
                    <Label htmlFor="make" className="text-xs sm:text-sm">Make</Label>
                    <Select
                      onValueChange={handleMakeChange}
                      value={make}
                      required
                    >
                      <SelectTrigger id="make" className="h-9 sm:h-10 text-xs sm:text-sm">
                        <SelectValue placeholder="Select a make" />
                      </SelectTrigger>
                      <SelectContent>
                        {makes.map((m) => (
                          <SelectItem key={m.name} value={m.name} className="text-xs sm:text-sm">
                            {m.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5 sm:space-y-2">
                    <Label htmlFor="model" className="text-xs sm:text-sm">Model</Label>
                    <Select
                      onValueChange={setModel}
                      value={model}
                      required
                      disabled={!make}
                    >
                      <SelectTrigger id="model" className="h-9 sm:h-10 text-xs sm:text-sm">
                        <SelectValue placeholder="Select a model" />
                      </SelectTrigger>
                      <SelectContent>
                        {models.map((m) => (
                          <SelectItem key={m} value={m} className="text-xs sm:text-sm">
                            {m}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Type and Sub-Type */}
              <div className="grid md:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                <div className="space-y-1.5 sm:space-y-2">
                  <Label htmlFor="type" className="text-xs sm:text-sm">Type</Label>
                  <Select
                    onValueChange={handleTypeChange}
                    value={type}
                    required
                    disabled={!category}
                  >
                    <SelectTrigger id="type" className="h-9 sm:h-10 text-xs sm:text-sm">
                      <SelectValue placeholder="Select a type" />
                    </SelectTrigger>
                    <SelectContent>
                      {category === 'vehicle' &&
                        vehicleTypes.map((item) => (
                          <SelectItem key={item} value={item} className="text-xs sm:text-sm">
                            {item}
                          </SelectItem>
                        ))}
                      {category === 'property' &&
                        propertyTypes.map((item) => (
                          <SelectItem key={item.name} value={item.name} className="text-xs sm:text-sm">
                            {item.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                {showPropertySubTypes && (
                  <div className="space-y-1.5 sm:space-y-2">
                    <Label htmlFor="subType" className="text-xs sm:text-sm">Sub-Type</Label>
                    <Select
                      onValueChange={setSubType}
                      value={subType}
                      required
                    >
                      <SelectTrigger id="subType" className="h-9 sm:h-10 text-xs sm:text-sm">
                        <SelectValue placeholder="Select a sub-type" />
                      </SelectTrigger>
                      <SelectContent>
                        {propertySubTypes.map((st) => (
                          <SelectItem key={st} value={st} className="text-xs sm:text-sm">
                            {st}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

            {/* Chama/Group Buying Opt-In */}
              <ChamaOptIn
                category={category}
                type={type}
                subType={subType}
                price={price}
                enabled={chamaEnabled}
                onToggle={setChamaEnabled}
                onChamaDataChange={setChamaData}
              />

              {/* Chama How It Works - For Sellers (only show when eligible) */}
             {category === 'property' && 
              parseFloat(price) >= 50000000 && 
              ['off plan', 'joint ventures', 'commercial property for sale', 'godowns and warehouses for sale']
                .some(t => t === (type || '').toLowerCase() || t === (subType || '').toLowerCase()) && (
                <ChamaHowItWorks audience="seller" />
              )}

              {/* Tags */}
              <div className="space-y-2 sm:space-y-3 md:space-y-4">
                <Label className="text-xs sm:text-sm">Tags</Label>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="text-xs sm:text-sm h-8 sm:h-9"
                    disabled={isSuggesting}
                    onClick={handleAISuggestions}
                  >
                    {isSuggesting ? (
                      <Loader2 className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4 animate-spin" />
                    ) : (
                      <Lightbulb className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                    )}
                    Get AI Suggestions
                  </Button>
                </div>
                <Input
                  placeholder="Add your own tags, comma-separated"
                  onKeyDown={handleTagInput}
                  className="h-9 sm:h-10 text-xs sm:text-sm"
                />
                {/* CSS FIX 12: Badge text sizing */}
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 sm:gap-2">
                    {tags.map((tag) => (
                      <Badge key={tag} variant="secondary" className="text-[10px] sm:text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit Button */}
              {/* CSS FIX 10: Button margin and sizing - !mt-8 → mt-4 sm:mt-6 */}
              <Button
                type="submit"
                size="lg"
                className="w-full mt-4 sm:mt-6 font-bold text-xs sm:text-sm h-10 sm:h-11"
                disabled={isSubmitting}
              >
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Proceed to Checkout
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
      <Footer />

     <UpgradeLimitModal
        open={showUpgradeModal}
        onClose={() => {
          console.log('[CreateListingPage] Upgrade modal closed');
          setShowUpgradeModal(false);
        }}
        tierInfo={tierInfo}
      />
    </div>
  );
}
